import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';
import * as XLSX from 'xlsx';
import { SeniorCenterApplicationListItem } from '../core/services/senior-center-application.service';
import { FormDateUtils } from '../shared/utils/form-date-utils';

export interface SeniorCenterExportRow {
  applicantName: string;
  applicantEmailAddress: string;
  rfidNumber: string;
  approvedDate: string;
}

const LOGO_PATH = 'assets/icons/BakerriplyTextLogo.svg';
let cachedLogoDataUrl: string | null = null;

function formatExportDate(value: string | null | undefined): string {
  if (!value) {
    return '';
  }

  const date = FormDateUtils.parseDateOnly(value);
  if (!date) {
    return '';
  }

  return FormDateUtils.formatDate(date, 'MM/DD/YYYY');
}

async function loadLogoDataUrl(): Promise<string | null> {
  if (cachedLogoDataUrl) {
    return cachedLogoDataUrl;
  }

  try {
    const response = await fetch(LOGO_PATH);
    if (!response.ok) {
      return null;
    }

    const svgText = await response.text();
    const blob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
    const objectUrl = URL.createObjectURL(blob);

    cachedLogoDataUrl = await new Promise<string>((resolve, reject) => {
      const image = new Image();
      image.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = image.naturalWidth || 428;
        canvas.height = image.naturalHeight || 80;
        const context = canvas.getContext('2d');
        if (!context) {
          URL.revokeObjectURL(objectUrl);
          reject(new Error('Unable to render BakerRipley logo'));
          return;
        }

        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        URL.revokeObjectURL(objectUrl);
        resolve(canvas.toDataURL('image/png'));
      };
      image.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Unable to load BakerRipley logo'));
      };
      image.src = objectUrl;
    });

    return cachedLogoDataUrl;
  } catch {
    return null;
  }
}

export function exportSeniorCenterRfidExcel(
  applications: SeniorCenterExportRow[],
  filename: string
): void {
  const worksheet = XLSX.utils.json_to_sheet(
    applications.map((app) => ({
      'Applicant Name': app.applicantName,
      'Applicant Email Address': app.applicantEmailAddress,
      'RFID Number': app.rfidNumber,
      'Approval Date': app.approvedDate,
    }))
  );
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'RFID Tags');
  XLSX.writeFile(workbook, filename);
}

async function drawQrCard(pdf: jsPDF, app: SeniorCenterExportRow, logoDataUrl: string | null): Promise<void> {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const centerX = pageWidth / 2;

  if (logoDataUrl) {
    pdf.addImage(logoDataUrl, 'PNG', centerX - 40, 12, 80, 15);
  } else {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(18);
    pdf.setTextColor(242, 111, 36);
    pdf.text('BakerRipley', centerX, 22, { align: 'center' });
  }

  pdf.setTextColor(17, 18, 21);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(16);
  pdf.text(app.applicantName, centerX, 42, { align: 'center', maxWidth: pageWidth - 20 });

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(12);
  pdf.setTextColor(68, 73, 85);
  pdf.text(app.applicantEmailAddress, centerX, 54, { align: 'center', maxWidth: pageWidth - 20 });

  const qrDataUrl = await QRCode.toDataURL(app.rfidNumber, {
    width: 420,
    margin: 1,
    errorCorrectionLevel: 'M',
  });
  const qrSize = 58;
  pdf.addImage(qrDataUrl, 'PNG', centerX - qrSize / 2, 68, qrSize, qrSize);

  pdf.setFontSize(10);
  pdf.setTextColor(68, 73, 85);
  pdf.text('Scan QR code to check in', centerX, 138, { align: 'center' });
}

export async function exportSeniorCenterQrPdf(
  applications: SeniorCenterExportRow[],
  filename: string
): Promise<void> {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [105, 148] });
  const logoDataUrl = await loadLogoDataUrl();

  for (let index = 0; index < applications.length; index += 1) {
    if (index > 0) {
      pdf.addPage();
    }

    await drawQrCard(pdf, applications[index], logoDataUrl);
  }

  pdf.save(filename);
}

export function toExportRow(
  app: SeniorCenterApplicationListItem,
  approvedDateOverride?: string | null
): SeniorCenterExportRow | null {
  const rfidNumber = (app.rfidNumber ?? '').trim();
  const approvedDate = approvedDateOverride ?? app.approvedDate ?? app.updatedAt;

  if (!rfidNumber || !approvedDate) {
    return null;
  }

  return {
    applicantName: app.applicantName,
    applicantEmailAddress: app.applicantEmailAddress,
    rfidNumber,
    approvedDate: formatExportDate(approvedDate),
  };
}
